import React, { useState, useMemo, useEffect } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from "material-react-table";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Autocomplete,
  MenuItem,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  useRegions,
  useZones,
  useInstances,
  useCreateInstance,
  useDeleteInstance,
  useMachineTypes,
  useImages,
  useStopInstance,
  useSuspendInstance,
  useStartInstance,
  useResumeInstance,
} from "../../../../../api/instances/instances";
import Back from "../../../../../components/Back";
import Title from "../../../../../components/Title";
import FormattedDateCell from "../../../../../components/FormattedDateCell";
import { useNavigate } from "react-router-dom";
import { useLoader } from "../../../../../context/LoaderContext";
import { useSnackbar } from "../../../../../context/SnackbarContext";

interface Instance {
  id: number | string;
  name: string;
  status: string;
  machine: string;
  created: string;
  platform: string;
  image?: any;
}

const DEFAULT_REGION = "us-east1";
const DEFAULT_ZONE = "us-east1-d";

const OS_OPTIONS = [
  { label: "Ubuntu", value: "Ubuntu" },

  { label: "Custom", value: "Custom" },
];

export default function Instances() {
  const { showLoader, hideLoader } = useLoader();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [selectedRegion, setSelectedRegion] = useState<string>(DEFAULT_REGION);
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageDialogData, setImageDialogData] = useState<any>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [createRegion, setCreateRegion] = useState<string>(DEFAULT_REGION);
  const [createZone, setCreateZone] = useState<string>(DEFAULT_ZONE);
  const [createMachine, setCreateMachine] = useState<string>("");
  const [createOs, setCreateOs] = useState<string>("Ubuntu");
  const [createImage, setCreateImage] = useState<any>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<Instance | null>(null);

  const { data: regions, isLoading: loadingRegions } = useRegions();
  const { data: zones, isLoading: loadingZones } = useZones(selectedRegion);

  const { data: createZones, isLoading: loadingCreateZones } =
    useZones(createRegion);

  const { data: machineTypes, isLoading: loadingMachineTypes } =
    useMachineTypes(createZone);
  const {
    data: images,
    isLoading: loadingImages,
    refetch: refetchImages,
  } = useImages(createOs);

  const { data, isLoading, isFetching, refetch } = useInstances(
    selectedRegion,
    selectedZone || undefined
  );
  const deleteInstance = useDeleteInstance();
  const stopInstance = useStopInstance();
  const suspendInstance = useSuspendInstance();
  const createInstance = useCreateInstance();
  const startInstance = useStartInstance();
  const resumeInstance = useResumeInstance();
  function validateGcpInstanceName(name) {
    if (!name) return "Instance name is required";
    if (!/^[a-z]([-a-z0-9]{0,61}[a-z0-9])?$/.test(name)) {
      return "Name must start with a letter, be 1-63 chars, only lowercase letters, numbers or dashes, end with letter or number";
    }
    if (name.includes("--")) {
      return "Name cannot contain two consecutive dashes (--)";
    }
    return "";
  }
  const handleNameChange = (e) => {
    setNewName(e.target.value);
  };

  const handleNameBlur = () => {
    const err = validateGcpInstanceName(newName);
    if (err) {
      showSnackbar(err, "error");
    }
  };
  function getAvailableActions(row: Instance, closeMenu: () => void) {
    const { name, zone, region, status } = row;

    const actions: JSX.Element[] = [];

    if (status === "TERMINATED") {
      actions.push(
        <MenuItem
          key="start"
          onClick={async () => {
            showLoader();
            try {
              await startInstance.mutateAsync({ name, zone, region });
              showSnackbar("Instance started", "success");
            } catch (error: any) {
              showSnackbar("Start failed: " + (error?.message || ""), "error");
            }
            hideLoader();
            closeMenu();
          }}
        >
          Start
        </MenuItem>
      );
    }

    if (status === "SUSPENDED") {
      actions.push(
        <MenuItem
          key="resume"
          onClick={async () => {
            showLoader();
            try {
              await resumeInstance.mutateAsync({ name, zone, region });
              showSnackbar("Instance resumed", "success");
            } catch (error: any) {
              showSnackbar("Resume failed: " + (error?.message || ""), "error");
            }
            hideLoader();
            closeMenu();
          }}
        >
          Resume
        </MenuItem>
      );
    }

    if (status === "RUNNING") {
      actions.push(
        <MenuItem
          key="stop"
          onClick={async () => {
            showLoader();
            try {
              await stopInstance.mutateAsync({ name, zone, region });
              showSnackbar("Instance stopped", "success");
            } catch (error: any) {
              showSnackbar("Stop failed: " + (error?.message || ""), "error");
            }
            hideLoader();
            closeMenu();
          }}
        >
          Stop
        </MenuItem>
      );
    }

    if (status === "RUNNING") {
      actions.push(
        <MenuItem
          key="suspend"
          onClick={async () => {
            showLoader();
            try {
              await suspendInstance.mutateAsync({ name, zone, region });
              showSnackbar("Instance suspended", "success");
            } catch (error: any) {
              showSnackbar(
                "Suspend failed: " + (error?.message || ""),
                "error"
              );
            }
            hideLoader();
            closeMenu();
          }}
        >
          Suspend
        </MenuItem>
      );
    }

    actions.push(
      <MenuItem
        key="view"
        onClick={() => {
          navigate(`/management/instances/${name}/view`);
          closeMenu();
        }}
      >
        View
      </MenuItem>
    );

    actions.push(
      <MenuItem
        key="delete"
        onClick={() => {
          setRowToDelete(row);
          setDeleteDialogOpen(true);
          closeMenu();
        }}
        sx={{ color: "error.main" }}
      >
        Delete
      </MenuItem>
    );

    return actions;
  }
  const handleOpenImageDialog = (image: any) => {
    setImageDialogData(image);
    setImageDialogOpen(true);
  };
  const handleCloseImageDialog = () => {
    setImageDialogOpen(false);
    setImageDialogData(null);
  };
  useEffect(() => {
    if (createOpen) {
      setNewName("");
      setCreateRegion(DEFAULT_REGION);
      setCreateZone(DEFAULT_ZONE);
      setCreateOs("Ubuntu");
      setCreateImage(null);
    }
  }, [createOpen]);

  useEffect(() => {
    if (createZones && createZones.length > 0) {
      setCreateZone(
        createZones.includes(DEFAULT_ZONE) ? DEFAULT_ZONE : createZones[0]
      );
    } else {
      setCreateZone("");
    }
  }, [createRegion, createZones]);

  useEffect(() => {
    if (machineTypes && machineTypes.length > 0) {
      setCreateMachine(
        machineTypes.includes("e2-micro") ? "e2-micro" : machineTypes[0]
      );
    } else {
      setCreateMachine("");
    }
  }, [createZone, machineTypes]);

  useEffect(() => {
    if (createOs) refetchImages();
    setCreateImage(null);
  }, [createOs, refetchImages]);

  useEffect(() => {
    setSelectedZone(null);
  }, [selectedRegion]);

  const columns = useMemo<MRT_ColumnDef<Instance>[]>(
    () => [
      { accessorKey: "id", header: "ID" },
      { accessorKey: "name", header: "Name" },
      { accessorKey: "status", header: "Status" },
      { accessorKey: "machine", header: "Machine Type" },
      { accessorKey: "platform", header: "CPU Platform" },
      {
        accessorKey: "image",
        header: "Image",
        Cell: ({ cell }) => {
          const value = cell.getValue<any>();
          if (!value) return "N/A";
          return (
            <span>
              <Tooltip title={value.link || ""}>
                <span
                  style={{
                    cursor: value.link ? "pointer" : "default",
                    color: "#1976d2",
                  }}
                >
                  {value.name}
                </span>
              </Tooltip>
              <IconButton
                size="small"
                onClick={() => handleOpenImageDialog(value)}
              >
                <InfoIcon fontSize="inherit" />
              </IconButton>
            </span>
          );
        },
      },
      {
        accessorKey: "zone",
        header: "Zone",
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          return (
            <span style={{ textTransform: "capitalize" }}>
              {value ?? "N/A"}
            </span>
          );
        },
      },
      {
        accessorKey: "region",
        header: "Region",
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          return (
            <span style={{ textTransform: "capitalize" }}>
              {value ?? "N/A"}
            </span>
          );
        },
      },
      {
        accessorKey: "created",
        header: "Created",
        Cell: ({ cell }) => (
          <FormattedDateCell value={cell.getValue<string>()} />
        ),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: data ?? [],
    enableColumnResizing: true,
    positionActionsColumn: "last",
    mrtTheme: (theme) => ({ baseBackgroundColor: theme.palette.background.paper }),
    muiTablePaperProps: {
      elevation: 0,
      sx: { borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden" },
    },
    muiTableHeadCellProps: {
      sx: { fontWeight: 700, bgcolor: "background.default", color: "text.secondary", letterSpacing: 0.2 },
    },
    initialState: {
      pagination: { pageSize: 25, pageIndex: 0 },
      density: "comfortable",
      showGlobalFilter: true,
      columnPinning: {
        right: ["mrt-row-actions"],
      },
    },
    muiTableBodyProps: { sx: { opacity: isLoading ? 0.5 : 1 } },
    renderTopToolbarCustomActions: () => (
      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
        <Autocomplete
          options={regions ?? []}
          loading={loadingRegions}
          value={selectedRegion || ""}
          onChange={(_, value) => setSelectedRegion(value || "")}
          sx={{ minWidth: 180 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Region"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingRegions ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Autocomplete
          options={zones ?? []}
          loading={loadingZones}
          value={selectedZone || ""}
          onChange={(_, value) => setSelectedZone(value || null)}
          sx={{ minWidth: 160 }}
          disabled={!selectedRegion}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Zone"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingZones ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Button variant="contained" onClick={() => setCreateOpen(true)}>
          Create Instance
        </Button>
        <Tooltip title="Refresh">
          <span>
            <IconButton
              onClick={async () => {
                showLoader();
                try {
                  await refetch();
                  // showSnackbar("Data refreshed", "success");
                } catch (e: any) {
                  showSnackbar(
                    "Failed to refresh: " + (e?.message || ""),
                    "error"
                  );
                }
                hideLoader();
              }}
              disabled={isFetching}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    ),
    enableRowActions: true,
    renderRowActionMenuItems: ({ row, closeMenu }) =>
      getAvailableActions(row.original, closeMenu),
  });

  const handleCreate = async () => {
    if (
      !newName.trim() ||
      !createRegion ||
      !createZone ||
      !createMachine ||
      !createImage ||
      !createOs
    ) {
      showSnackbar("Please fill all required fields", "error");
      return;
    }
    showLoader();
    try {
      await createInstance.mutateAsync({
        name: newName.trim(),
        machine: createMachine,
        image: createImage.link,
        zone: createZone,
        region: createRegion,
        os: createOs,
      });
      showSnackbar("Instance created!", "success");
      setCreateOpen(false);
      setNewName("");
    } catch (error: any) {
      showSnackbar("Create failed: " + (error?.message || ""), "error");
    }
    hideLoader();
  };

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    showLoader();
    try {
      await deleteInstance.mutateAsync({
        name: rowToDelete.name,
        zone: rowToDelete.zone,
      });
      showSnackbar("Instance deleted", "success");
    } catch (error: any) {
      showSnackbar("Delete failed: " + (error?.message || ""), "error");
    }
    setDeleteDialogOpen(false);
    setRowToDelete(null);
    hideLoader();
  };

  return (
    <>
      <Title>Instances</Title>
      <MaterialReactTable table={table} />
      <Back />

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)}>
        <DialogTitle>Create New Instance</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, width: 400 }}
        >
          <TextField
            label="Instance Name"
            sx={{ mt: 2 }}
            fullWidth
            value={newName}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            required
          />
          <Autocomplete
            options={OS_OPTIONS}
            value={OS_OPTIONS.find((opt) => opt.value === createOs) || null}
            onChange={(_, value) => setCreateOs(value?.value || "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label="OS Type"
                required
                InputProps={params.InputProps}
              />
            )}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
          />
          <Autocomplete
            options={images}
            loading={loadingImages}
            value={createImage}
            onChange={(_, value) => setCreateImage(value || null)}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) =>
              option && value && option.link === value.link
            }
            renderOption={(props, option) => {
              let title = option.name;
              let details = option.description || "";

              const descRegex = /Canonical, Ubuntu, ([^,]+), ([^ ]+) (.*)/i;
              const match = descRegex.exec(option.description || "");
              if (match) {
                title = `Ubuntu ${match[1]}`;
                details = `${match[2]} ${match[3]}`.replace(/^,/, "").trim();
              }

              return (
                <li {...props} key={option.link}>
                  <Box>
                    <div style={{ fontWeight: 600 }}>{title}</div>
                    <div style={{ fontSize: 12, color: "#888" }}>{details}</div>
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Image"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingImages ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            disabled={loadingImages || !createOs}
          />

          <Autocomplete
            options={regions ?? []}
            loading={loadingRegions}
            value={createRegion || ""}
            onChange={(_, value) => setCreateRegion(value || "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Region"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingRegions ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Autocomplete
            options={createZones ?? []}
            loading={loadingCreateZones}
            value={createZone || ""}
            onChange={(_, value) => setCreateZone(value || "")}
            disabled={!createRegion}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Zone"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCreateZones ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Autocomplete
            options={machineTypes ?? []}
            loading={loadingMachineTypes}
            value={createMachine || ""}
            onChange={(_, value) => setCreateMachine(value || "")}
            disabled={!createZone}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Machine Type"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingMachineTypes ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={
              !newName.trim() ||
              !createRegion ||
              !createZone ||
              !createMachine ||
              !createOs ||
              !createImage ||
              loadingRegions ||
              loadingCreateZones ||
              loadingMachineTypes ||
              loadingImages
            }
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 1 }}>
            Are you sure you want to delete
            <b> {rowToDelete?.name}</b>? This action cannot be undone!
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={imageDialogOpen} onClose={handleCloseImageDialog}>
        <DialogTitle>Image Details</DialogTitle>
        <DialogContent>
          {imageDialogData ? (
            <Box sx={{ minWidth: 300 }}>
              <Box sx={{ mb: 1 }}>
                <b>Name:</b> {imageDialogData.name}
              </Box>
              <Box sx={{ mb: 1 }}>
                <b>Family:</b> {imageDialogData.family || "—"}
              </Box>
              <Box sx={{ mb: 1 }}>
                <b>Description:</b> {imageDialogData.description || "—"}
              </Box>
              {imageDialogData.link && (
                <Box sx={{ mb: 1 }}>
                  <b>Link:</b>{" "}
                  <a
                    href={imageDialogData.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {imageDialogData.link}
                  </a>
                </Box>
              )}
            </Box>
          ) : (
            <Typography>No image data</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImageDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
